import { Doc, Id } from "@cvx/_generated/dataModel";
import { Info, Loader2, Sparkles } from "lucide-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { usePaginatedQuery, useQuery } from "convex/react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Route as DashboardRoute } from "@/routes/_app/_auth/dashboard/_layout.index";
import { Input } from "@/components/ui/input";
import { MARKETING_EVENTS } from "@/constants/analyticsEvents";
import PartnershipApplication from "@/components/Partnership/PartnershipApplication";
import PlatformIcon from "@/components/Generic/PlatformIcon";
import { Progress } from "@/components/ui/progress";
import { TooltipProvider } from "@/components/ui/tooltip";
import { api } from "@cvx/_generated/api";
import { cn } from "@/utils/misc";
import siteConfig from "~/site.config";
import { useAccount } from "@/state/useAccount";
import { useMixpanel } from "@/hooks";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/partnerships/apply",
)({
  component: ApplyPartnership,
  beforeLoad: () => ({
    title: `${siteConfig.siteTitle} - Apply for Partnership`,
    // headerTitle: "Partnership Application",
    // headerDescription: "Apply to partner with a brand",
  }),
  validateSearch: (search: Record<string, unknown>) => {
    return {
      selectedCampaignId:
        (search.selectedCampaignId as Id<"campaigns"> | undefined) || undefined,
    };
  },
});

function ApplyPartnership() {
  const [searchQuery, setSearchQuery] = useState("");
  const [showExpired, setShowExpired] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<
    (Doc<"campaigns"> & { app: Doc<"apps"> }) | null
  >(null);
  //   const [isChatOpen, setIsChatOpen] = useState(false);

  const navigate = useNavigate();
  const search = Route.useSearch();
  const { selectedCampaignId } = search;
  const mixpanel = useMixpanel();

  const {
    pendingCampaignId,
    clearPendingCampaignId,
    creatorId,
    setContractId,
  } = useAccount();

  // Get user data to check placeOfResidence
  const user = useQuery(api.app.getCurrentUser);

  // Check if we should use pending campaign ID and update URL
  useEffect(() => {
    if (!selectedCampaignId && pendingCampaignId) {
      navigate({
        search: { selectedCampaignId: pendingCampaignId },
        replace: true,
      });
      clearPendingCampaignId();
    }
  }, [selectedCampaignId, pendingCampaignId, navigate, clearPendingCampaignId]);

  // Use the campaign ID from URL or pending
  const effectiveCampaignId = selectedCampaignId || pendingCampaignId;

  //   const creator = useQuery(api.creators.getCreatorByCreatorId, {
  //     creatorId: creatorId as Id<"creators">,
  //   });

  //   const hasPartneredWithApp = useQuery(
  //     api.helpers.isCampaignFounderOrCampaignCreator,
  //     {
  //       accountType: "creator",
  //       campaignId: effectiveCampaignId,
  //     },
  //   );

  const {
    results: campaignsAvailableForPartnerships,
    isLoading: isLoadingCampaignsAvailableForPartnerships,
  } = usePaginatedQuery(
    api.partnerships.getPaginatedCampaignsAvailableForPartnerships,
    {},
    { initialNumItems: 10 },
  );

  const totalCreatorsCount = useQuery(api.partnerships.getTotalCreatorsCount);

  // Fetch user's active partnerships to filter them out
  const { results: userPartnerships } = usePaginatedQuery(
    api.partnerships.getPaginatedCreatorPartnershipsForCreator,
    { type: "contracts" },
    { initialNumItems: 100 },
  );

  // Filter out campaigns the user has already enrolled in
  const enrolledCampaignIds = new Set(
    userPartnerships?.map((p) => p.campaignId) || [],
  );

  // Create mapping from campaignId to partnershipId for enrolled campaigns
  const campaignIdToPartnershipId = useMemo(() => {
    const map = new Map<Id<"campaigns">, Id<"creatorContracts">>();
    userPartnerships?.forEach((partnership) => {
      if (partnership.campaignId && partnership.type === "contract") {
        map.set(
          partnership.campaignId,
          partnership._id as Id<"creatorContracts">,
        );
      }
    });
    return map;
  }, [userPartnerships]);

  // Hardcoded creator avatars for display
  const creatorAvatars = [
    "https://images-na.ssl-images-amazon.com/images/S/amzn-author-media-prod/9albp97vrnqisvn3ebc97oe237.jpg",
    "https://yt3.googleusercontent.com/5YKK31dACR6q_u2zaK7t-XgFYNe9IFFAEzFiAa5dSuhZ7qFbDVq9kP90Zg86xLe88QMWP9BBwg=s900-c-k-c0x00ffffff-no-rj",
    "https://i.pinimg.com/280x280_RS/a4/ac/9e/a4ac9eac3f5d25372d8b3eb606058d8d.jpg",
  ];

  // Filter campaigns based on user's country and enrollment status (for Available tab)
  const filteredCampaigns = useMemo(() => {
    if (!campaignsAvailableForPartnerships) {
      return [];
    }

    const now = Date.now();

    const filtered = campaignsAvailableForPartnerships.filter((campaign) => {
      // Filter out campaigns the user has already enrolled in
      if (enrolledCampaignIds.has(campaign._id)) {
        return false;
      }

      // Filter out expired campaigns if showExpired is false
      if (!showExpired && campaign.endDate && campaign.endDate < now) {
        return false;
      }

      // Filter by country if user has set their residence
      if (user?.placeOfResidence) {
        // If no eligible countries specified, show to all
        if (
          !campaign.eligibleCountries ||
          campaign.eligibleCountries.length === 0
        ) {
          return true;
        }
        // Check if user's country is in the eligible countries list
        return campaign.eligibleCountries.includes(user.placeOfResidence);
      }

      return true;
    });

    // Sort by CPM (descending) - campaigns with higher CPM first
    return filtered.sort((a, b) => {
      const cpmA = a.predefinedCpm || 0;
      const cpmB = b.predefinedCpm || 0;
      return cpmB - cpmA;
    });
  }, [
    campaignsAvailableForPartnerships,
    user?.placeOfResidence,
    enrolledCampaignIds,
    showExpired,
  ]);

  // All campaigns sorted with enrolled first, then non-enrolled
  const allCampaignsSorted = useMemo(() => {
    if (!campaignsAvailableForPartnerships) {
      return [];
    }

    const now = Date.now();

    // Filter by country if user has set their residence
    const filtered = campaignsAvailableForPartnerships.filter((campaign) => {
      // Filter out expired campaigns if showExpired is false
      if (!showExpired && campaign.endDate && campaign.endDate < now) {
        return false;
      }

      if (user?.placeOfResidence) {
        // If no eligible countries specified, show to all
        if (
          !campaign.eligibleCountries ||
          campaign.eligibleCountries.length === 0
        ) {
          return true;
        }
        // Check if user's country is in the eligible countries list
        return campaign.eligibleCountries.includes(user.placeOfResidence);
      }

      return true;
    });

    // Separate enrolled and non-enrolled campaigns
    const enrolled: (Doc<"campaigns"> & { app: Doc<"apps"> })[] = [];
    const notEnrolled: (Doc<"campaigns"> & { app: Doc<"apps"> })[] = [];

    filtered.forEach((campaign) => {
      if (enrolledCampaignIds.has(campaign._id)) {
        enrolled.push(campaign);
      } else {
        notEnrolled.push(campaign);
      }
    });

    // Sort each group by CPM (descending)
    enrolled.sort((a, b) => {
      const cpmA = a.predefinedCpm || 0;
      const cpmB = b.predefinedCpm || 0;
      return cpmB - cpmA;
    });

    notEnrolled.sort((a, b) => {
      const cpmA = a.predefinedCpm || 0;
      const cpmB = b.predefinedCpm || 0;
      return cpmB - cpmA;
    });

    // Return enrolled first, then non-enrolled
    return [...enrolled, ...notEnrolled];
  }, [
    campaignsAvailableForPartnerships,
    user?.placeOfResidence,
    enrolledCampaignIds,
    showExpired,
  ]);

  useEffect(() => {
    if (effectiveCampaignId && campaignsAvailableForPartnerships.length > 0) {
      const campaign = campaignsAvailableForPartnerships.find(
        (campaign) => campaign._id === effectiveCampaignId,
      );
      if (campaign) {
        setSelectedCampaign(campaign);
      }
    } else if (!effectiveCampaignId) {
      // Clear selected campaign when there's no campaign ID
      setSelectedCampaign(null);
    }
  }, [effectiveCampaignId, campaignsAvailableForPartnerships]);

  // Render campaign card component (reusable for both tabs)
  const renderCampaignCard = (
    campaign: Doc<"campaigns"> & { app: Doc<"apps"> },
    isEnrolled: boolean,
    partnershipId?: Id<"creatorContracts">,
  ) => {
    // Calculate time left and progress
    const now = Date.now();
    const endDate = campaign.endDate || null;
    let timeLeftText = "";
    let timeProgress = 0;
    let daysLeft = 0;

    if (endDate) {
      const timeLeft = endDate - now;
      daysLeft = Math.ceil(timeLeft / (1000 * 60 * 60 * 24));

      if (daysLeft > 0) {
        timeLeftText = daysLeft === 1 ? "1 day left" : `${daysLeft} days left`;
      } else {
        timeLeftText = "Expired";
      }

      // Calculate progress (assuming 30 days total for visualization)
      const totalDays = 30;
      const daysElapsed = totalDays - daysLeft;
      timeProgress = Math.min(
        Math.max((daysElapsed / totalDays) * 100, 0),
        100,
      );
    }

    const cpmRate = campaign.predefinedCpm || null;

    // Deduplicate YouTube platforms - show only one YouTube icon if both youtube and youtube-shorts exist
    const displayChannels = campaign.allowedChannels
      ? campaign.allowedChannels.filter((channel) => {
          // If this is youtube-shorts and youtube exists, skip it
          if (
            channel === "youtube-shorts" &&
            campaign.allowedChannels?.includes("youtube")
          ) {
            return false;
          }
          return true;
        })
      : [];

    return (
      <Card
        key={campaign._id}
        className="cursor-pointer p-6 transition-all hover:bg-secondary/50 hover:shadow-md"
        onClick={() => {
          if (isEnrolled && partnershipId) {
            // Switch to this campaign and navigate to dashboard
            setContractId(partnershipId);
            navigate({ to: DashboardRoute.fullPath });
          } else {
            // Track campaign review for onboarding widget
            localStorage.setItem("hasReviewedCampaign", "true");

            // Track campaign click in Mixpanel
            mixpanel.track(MARKETING_EVENTS.CAMPAIGN_CLICKED, {
              campaign_id: campaign._id,
              campaign_name: campaign.name,
              app_id: campaign.appId,
              app_name: campaign.app.name,
              campaign_type: campaign.type,
              cpm_rate: campaign.predefinedCpm,
              has_prize_pool: !!campaign.prizePool,
              prize_pool: campaign.prizePool,
              allowed_channels: campaign.allowedChannels,
              application_required: !campaign.applicationNotRequired,
              user_country: user?.placeOfResidence,
              source: "partnerships_apply_page",
              timestamp: new Date().toISOString(),
            });

            navigate({
              search: { selectedCampaignId: campaign._id },
              replace: true,
            });
          }
        }}
      >
        <div className="space-y-4">
          {/* Header with logo and names */}
          <div className="flex items-start gap-3">
            <img
              className="h-12 w-12 rounded-lg object-cover"
              alt={campaign.app?.name ?? ""}
              src={campaign.app.logo ?? ""}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-semibold text-primary truncate">
                  {campaign.name ?? ""}
                </h3>
                {isEnrolled && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20 rounded-full whitespace-nowrap">
                    Enrolled
                  </span>
                )}
              </div>
              <p className="text-sm text-primary/60">
                {campaign.app?.name ?? ""}
              </p>
            </div>
          </div>

          {/* Platforms and CPM Badge */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs text-primary/60">Platforms:</span>
              <div className="flex gap-1.5">
                {displayChannels.length > 0 ? (
                  displayChannels.map((platform) => (
                    <div
                      key={platform}
                      className="flex items-center justify-center w-7 h-7 rounded-full bg-secondary/80"
                    >
                      <PlatformIcon platform={platform} />
                    </div>
                  ))
                ) : (
                  <span className="text-xs text-primary/40">All platforms</span>
                )}
              </div>
            </div>

            {cpmRate && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-full">
                <span className="text-xs font-semibold text-green-600 dark:text-green-400">
                  ${(cpmRate * 1000).toLocaleString()} / 1M views
                </span>
              </div>
            )}
          </div>

          {/* Time Left Progress */}
          {endDate && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-primary/60">Time left:</span>
                <span className="font-semibold text-primary flex items-center gap-1">
                  {timeLeftText === "Expired" ? (
                    <span className="text-red-500">⚠️ {timeLeftText}</span>
                  ) : (
                    <>
                      <span className={daysLeft <= 3 ? "text-yellow-500" : ""}>
                        {daysLeft <= 3 && "⚠️ "}
                        {timeLeftText}
                      </span>
                    </>
                  )}
                </span>
              </div>
              <Progress value={timeProgress} className="h-2" />
            </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <div className="flex h-full w-full flex-col gap-8 bg-secondary p-8 dark:bg-black">
      <div
        className={cn(
          "mx-auto w-full",
          selectedCampaign ? "max-w-7xl" : "max-w-3xl",
        )}
      >
        {isLoadingCampaignsAvailableForPartnerships ? (
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <Loader2 className="h-12 w-12 text-primary/60 animate-spin mb-4" />
            <h3 className="text-xl font-semibold text-primary mb-2">
              Loading Campaigns...
            </h3>
            <p className="text-primary/60 max-w-md">
              We're fetching the latest brand deals for you.
            </p>
          </div>
        ) : !selectedCampaign ? (
          <div className="space-y-6">
            {creatorId ? (
              <>
                <div className="flex flex-col items-start gap-2">
                  <h2 className="text-2xl font-bold text-primary">
                    Your Campaigns
                  </h2>
                  <p className="text-primary/60">
                    View all campaigns you're enrolled in or can enroll in
                  </p>
                </div>

                <div className="flex items-center gap-4">
                  <Input
                    type="text"
                    placeholder="Search campaigns..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-transparent"
                  />
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="show-expired"
                      checked={showExpired}
                      onCheckedChange={(checked) =>
                        setShowExpired(checked === true)
                      }
                    />
                    <label
                      htmlFor="show-expired"
                      className="text-sm text-primary/80 cursor-pointer select-none"
                    >
                      Show expired campaigns
                    </label>
                  </div>
                </div>

                <TooltipProvider>
                  <div className="space-y-4">
                    {allCampaignsSorted.filter((campaign) =>
                      campaign.name
                        .toLowerCase()
                        .includes(searchQuery.toLowerCase()),
                    ).length === 0 ? (
                      <div className="flex flex-col items-center justify-center p-12 text-center">
                        <div className="rounded-full bg-secondary/80 p-4 mb-4">
                          <Info className="h-8 w-8 text-primary/60" />
                        </div>
                        <h3 className="text-xl font-semibold text-primary mb-2">
                          No Results Found
                        </h3>
                        <p className="text-primary/60 max-w-md">
                          No campaigns match your search query. Try adjusting
                          your search terms.
                        </p>
                      </div>
                    ) : (
                      allCampaignsSorted
                        .filter((campaign) =>
                          campaign.name
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase()),
                        )
                        .map((campaign) => {
                          const isEnrolled = enrolledCampaignIds.has(
                            campaign._id,
                          );
                          const partnershipId = campaignIdToPartnershipId.get(
                            campaign._id,
                          );
                          return renderCampaignCard(
                            campaign,
                            isEnrolled,
                            partnershipId,
                          );
                        })
                    )}
                  </div>
                </TooltipProvider>
              </>
            ) : (
              // Show only available campaigns view for non-creators
              <>
                {filteredCampaigns.length > 0 && (
                  <>
                    <div className="flex flex-col items-start gap-2">
                      <h2 className="text-2xl font-bold text-primary">
                        Available Brand deals
                      </h2>
                      <div className="flex items-center gap-3">
                        <div className="flex -space-x-3">
                          {creatorAvatars.map((avatarUrl, index) => (
                            <img
                              key={index}
                              src={avatarUrl}
                              alt={`Creator ${index + 1}`}
                              className="h-10 w-10 rounded-full border-2 border-white dark:border-black object-cover"
                            />
                          ))}
                        </div>
                        <p className="text-base text-primary/60">
                          Join {totalCreatorsCount ?? "..."} creators
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <Input
                        type="text"
                        placeholder="Search campaigns..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full bg-transparent"
                      />
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="show-expired-non-creator"
                          checked={showExpired}
                          onCheckedChange={(checked) =>
                            setShowExpired(checked === true)
                          }
                        />
                        <label
                          htmlFor="show-expired-non-creator"
                          className="text-sm text-primary/80 cursor-pointer select-none"
                        >
                          Show expired campaigns
                        </label>
                      </div>
                    </div>
                  </>
                )}

                <TooltipProvider>
                  <div className="space-y-4">
                    {filteredCampaigns.length === 0 ? (
                      <div className="flex flex-col items-center justify-center p-12 text-center">
                        <div className="rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 p-6 mb-6">
                          <Sparkles className="h-12 w-12 text-purple-500" />
                        </div>
                        <h3 className="text-2xl font-bold text-primary mb-3">
                          No Campaigns in Your Region Yet 🔜
                        </h3>
                        <p className="text-primary/60 max-w-md mb-6">
                          Right now, there are no available campaigns in your
                          country. We'll email you as soon as one becomes
                          available!
                        </p>
                        <p className="text-sm text-primary/50 max-w-md mb-6">
                          In the meantime, level up your content creation skills
                          with our free tools for content creators!
                        </p>
                        <Button
                          onClick={() => {
                            // Track click event in Mixpanel
                            mixpanel.track(
                              MARKETING_EVENTS.TRY_FREE_AI_TOOLS_CLICKED,
                              {
                                source: "No Campaigns Empty State",
                                location: window.location.pathname,
                              },
                            );
                            window.open(
                              `${window.location.origin}/grow`,
                              "_blank",
                            );
                          }}
                          className="relative bg-gradient-to-r from-purple-500/90 to-pink-500/90 text-white font-medium rounded-lg px-6 py-3 shadow-lg transition-all hover:shadow-xl hover:scale-105"
                        >
                          <Sparkles className="h-4 w-4 mr-2" />
                          Try our free AI tools
                        </Button>
                      </div>
                    ) : filteredCampaigns.filter((campaign) =>
                        campaign.name
                          .toLowerCase()
                          .includes(searchQuery.toLowerCase()),
                      ).length === 0 ? (
                      <div className="flex flex-col items-center justify-center p-12 text-center">
                        <div className="rounded-full bg-secondary/80 p-4 mb-4">
                          <Info className="h-8 w-8 text-primary/60" />
                        </div>
                        <h3 className="text-xl font-semibold text-primary mb-2">
                          No Results Found
                        </h3>
                        <p className="text-primary/60 max-w-md">
                          No campaigns match your search query. Try adjusting
                          your search terms or browse all available campaigns.
                        </p>
                      </div>
                    ) : (
                      filteredCampaigns
                        .filter((campaign) =>
                          campaign.name
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase()),
                        )
                        .map((campaign) => renderCampaignCard(campaign, false))
                    )}
                  </div>
                </TooltipProvider>
              </>
            )}
          </div>
        ) : (
          <div>
            <PartnershipApplication
              selectedCampaign={selectedCampaign}
              clearPendingCampaignId={clearPendingCampaignId}
              onSelectCampaign={(campaignId) => {
                navigate({
                  search: { selectedCampaignId: campaignId },
                  replace: true,
                });
              }}
            />

            {/* {creator && hasPartneredWithApp && (
              <FloatingChat
                creator={creator}
                campaignId={selectedCampaign._id}
                isChatOpen={isChatOpen}
                setIsChatOpen={setIsChatOpen}
              />
            )} */}
          </div>
        )}
      </div>
    </div>
  );
}
